// src/utils/share.js
const encoded = (s) => encodeURIComponent(s);
const PUBLIC_FALLBACK = "http://192.168.1.177:5002";
const isLocalhostBase = (value) => /localhost|127\.0\.0\.1/i.test(String(value || ""));
const isProdBuild = () => String(process.env.NODE_ENV || "").toLowerCase() === "production";

function toHttpsAbsolute(base) {
    const raw = String(base || "").trim().replace(/\/+$/, "");
    if (!raw) return "";
    return raw.startsWith("http://") ? raw.replace(/^http:\/\//i, "https://") : raw;
}

function resolvePublicBase(explicitBase) {
    const candidates = [
        explicitBase,
        process.env.REACT_APP_PUBLIC_BASE_URL,
        process.env.REACT_APP_FRONTEND_URL,
        typeof window !== "undefined" ? window.location?.origin : "",
    ];
    for (const candidate of candidates) {
        const normalized = toHttpsAbsolute(candidate);
        if (!normalized) continue;
        if (isLocalhostBase(normalized)) continue;
        return normalized;
    }
    return PUBLIC_FALLBACK;
}

function resolveApiBase({ apiBaseUrl, publicBaseUrl }) {
    const api = toHttpsAbsolute(apiBaseUrl);
    if (api && !isLocalhostBase(api)) return api;
    return `${resolvePublicBase(publicBaseUrl)}/api`;
}

export function normalizeShareHost(url) {
    const raw = String(url || "").trim();
    if (!raw) return "";
    try {
        const parsed = new URL(raw);
        if (
            isProdBuild() &&
            parsed.hostname === "in-cooking.com" &&
            parsed.pathname.startsWith("/api/share/")
        ) {
            parsed.protocol = "https:";
            parsed.hostname = "www.in-cooking.com";
        }
        return parsed.toString();
    } catch {
        return raw;
    }
}

export function appendQuery(url, key, value) {
    const raw = String(url || "").trim();
    if (!raw || value === undefined || value === null || value === "") return raw;
    try {
        const parsed = new URL(raw);
        parsed.searchParams.set(String(key), String(value));
        return parsed.toString();
    } catch {
        return raw;
    }
}

export function buildCanonicalRecipeUrl({ appPublicUrl, canonicalPath }) {
    const publicBase = resolvePublicBase(appPublicUrl);
    const path = String(canonicalPath || "").startsWith("/") ? canonicalPath : `/${canonicalPath || ""}`;
    return `${publicBase}${path}`;
}

export function buildRecipeSharePreviewUrl({ apiBaseUrl, lang, slugOrId }) {
    const base = resolveApiBase({ apiBaseUrl });
    const safeLang = encodeURIComponent(String(lang || "en").toLowerCase());
    const safeSlug = encodeURIComponent(String(slugOrId || "").trim());
    return `${base}/share/recipe/${safeLang}/${safeSlug}`;
}

/**
 * Χρησιμοποιούμε ΠΑΝΤΟΥ το ssrUrl για να διαβάζουν OG οι scrapers.
 * Εξαιρέσεις: email και copy (που δεν χρειάζονται crawler).
 */
export function shareTargetsByNetwork({
    title,
    slugUrl,
    ssrUrl,
    image,
    facebookAppId,
    appPublicUrl,
    facebookVersion,
}) {
    const isMobileUA = /Mobi|Android|iPhone|iPad/i.test(
        typeof navigator !== "undefined" ? navigator.userAgent : ""
    );
    const waBase = isMobileUA
        ? "https://api.whatsapp.com/send?text="
        : "https://web.whatsapp.com/send?text=";

    const canonicalVisible = slugUrl || ssrUrl;
    const normalizedSsrUrl = normalizeShareHost(ssrUrl);
    const facebookSsrUrl = appendQuery(normalizedSsrUrl, "v", facebookVersion);
    const q = `${title ? `${title} ` : ""}${normalizedSsrUrl}`.trim();
    const appRedirect = `${resolvePublicBase(appPublicUrl)}/`;
    const messengerTarget = facebookAppId
        ? `https://www.facebook.com/dialog/send?link=${encoded(facebookSsrUrl)}&app_id=${encoded(facebookAppId)}&redirect_uri=${encoded(appRedirect)}`
        : `https://www.messenger.com/share?link=${encoded(facebookSsrUrl)}`;

    return {
        // ΟΛΑ τα κοινωνικά → SSR URL
        facebook: `https://www.facebook.com/sharer.php?u=${encoded(facebookSsrUrl)}`,
        messenger: messengerTarget,
        pinterest: `https://www.pinterest.com/pin/create/button/?url=${encoded(normalizedSsrUrl)}&media=${encoded(image)}&description=${encoded(title)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded(normalizedSsrUrl)}`,
        reddit: `https://www.reddit.com/submit?url=${encoded(normalizedSsrUrl)}&title=${encoded(title)}`,
        telegram: `https://t.me/share/url?url=${encoded(normalizedSsrUrl)}&text=${encoded(q)}`,
        x: `https://twitter.com/intent/tweet?url=${encoded(normalizedSsrUrl)}&text=${encoded(q)}&hashtags=recipes,cooking`,
        whatsapp: `${waBase}${encoded(q)}`,
        viber: `viber://forward?text=${encoded(q)}`,

        // μη-crawler προορισμοί
        email: `mailto:?subject=${encoded(title)}&body=${encoded(`${q}\n${canonicalVisible}`.trim())}`,
    };
}
